import { Controller } from '@hotwired/stimulus'
import { FetchRequest } from '@rails/request.js'
import ReactRailsUJS from 'react_ujs'
import { debounce } from 'underscore'

export default class Autosave extends Controller {
  static values = {
    url: String,
    finalizeUrl: String,
    debounceTime: { type: Number, default: 900 }
  }

  connect() {
    this.firstRequest = true
    this.requestsInFlight = 0
    this.jitterProtectedElements = new Array()
    this.fieldsPendingUpdate = new Set()
    
    // Create a debounced save function
    this.debouncedSave = debounce(this.saveChanges, this.debounceTimeValue)

    this.element.querySelectorAll('input, textarea, select').forEach(element => {
      element.addEventListener('change', this.inputChanged)
    })

    // React Components are mounted after Autosave is connected.
    window.addEventListener('react-date-time:mounted', debounce(this.attachReactDateTimePickerListeners, 300, true))

    document.addEventListener('turbo:before-morph-element', this.preventInputElementJitter)
    document.addEventListener('turbo:before-render', this.unmmountReactComponents)

    console.log('SuperForm: auto-save enabled.')
  }

  disconnect() {
    this.element.querySelectorAll('input, textarea, select').forEach(element => {
      element.removeEventListener('change', this.inputChanged)
    })

    this.element.querySelectorAll('input[type="hidden"].react-date-time').forEach(element => {
      element.removeEventListener('react-date-time:changed', this.inputChanged)
    })

    window.removeEventListener('react-date-time:mounted', this.attachReactDateTimePickerListeners)

    document.removeEventListener('turbo:before-morph-element', this.preventInputElementJitter)
    document.removeEventListener('turbo:before-render', this.unmmountReactComponents)
  }

  attachReactDateTimePickerListeners = () => {
    this.element.querySelectorAll('input[type="hidden"].react-date-time').forEach(element => {
      element.addEventListener('change', this.inputChanged)
    })
  }

  inputChanged = event => {
    let target = event.target
    let elementName = target.name
    this.fieldsPendingUpdate.add(elementName)

    this.jitterProtectedElements.push(elementName)
    // Pop the oldest input name off the jitterProtectedElements array after 3x the debounce time.
    setTimeout(() => {this.jitterProtectedElements.shift() }, this.debounceTimeValue * 3)

    // Remove error classes from elements because jitter protection will prevent them from updating.
    this.element.querySelectorAll(`[name="${elementName}"]`).forEach(el => el.classList.remove('is-invalid'))
    const closestContainer = target.closest('.super-form__field, fieldset')
    closestContainer?.querySelectorAll('.invalid-feedback').forEach(el => el.remove())

    this.debouncedSave()
  }
  
  saveChanges = () => {
    // Copy the changed fields to a new array to prevent mutation during iteration.
    const updateBatch = Array.from(this.fieldsPendingUpdate)
    // Clear the changed fields tracking object
    this.fieldsPendingUpdate = new Set()

    const data = new FormData()
    
    // Process all tracked elements and add them to the form data
    updateBatch.forEach(name => {
      const elements = this.element.querySelectorAll(`[name="${name}"]`)
      Array.from(elements).forEach(el => {
        if (el.type === 'checkbox' || el.type === 'radio') {
          if (el.checked) {
            data.append(el.name, el.value)
          }
        } else {
          data.append(el.name, el.value)
        }
      })
    })

    // Use the first Autosave request to send all the initial values of prefilled fields.
    // If we move prefilled field evaluation `evaluate_and_assign_formula_fields` we can remove this.
    if (this.firstRequest) {
      this.element.querySelectorAll('input[data-prefilled="true"]').forEach(element => {
        data.append(element.name, element.value)
      })
    }
    
    const request = new FetchRequest('patch', this.urlValue, {
      body: data,
      responseKind: 'json'
    })
    
    this.withRequestCount(request.perform()).then(response => {
      document.dispatchEvent(new CustomEvent('super-form:autosave:started', { detail: { request: request } }))

      if (response.ok) {
        document.dispatchEvent(new CustomEvent('super-form:autosave:success', { detail: { response: response } }))
      }
    }).catch(error => {
      console.error('SuperForm: autosave failed', error)
      document.dispatchEvent(new CustomEvent('super-form:autosave:failed', { detail: { error: error } }))
    })
  }

  // Prevents jitter when an input element is morphed by Turbo.
  preventInputElementJitter = event => {
    const detail = event.detail
    const newEl = detail.newElement
    if (!newEl?.name) return

    const target = event.target

    if (this.jitterProtectedElements.includes(newEl.name)) {
      event.preventDefault()
      console.log(`SuperForm#preventInputElementJitter: prevented jitter for recently updated element ${newEl.name}`)
    } else if(!target.readOnly && target.name === document.activeElement.name) {
      event.preventDefault()
      console.log(`SuperForm#preventInputElementJitter: prevented jitter for active element ${newEl.name}`)
    }
  }

  // If there are no requests in flight, finalize the form. Otherwise, wait for the last autosave request to complete.
  finalize = () => {
    if (this.requestsInFlight === 0) {
      this.requestFinalize()
    } else {
      document.addEventListener('turbo:load', () => this.requestFinalize(), { once: true })
    }
  }

  requestFinalize = () => {
    this.element.action = this.finalizeUrlValue
    this.element.requestSubmit()
  }

  unmmountReactComponents = () => ReactRailsUJS.unmountComponents()

  withRequestCount = (request) => {
    this.requestsInFlight++

    return request.then(response => {
      this.firstRequest = false
      return response
    }).finally(() => {
      this.requestsInFlight--
    })
  }
}

export { Autosave }
